import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SESSION_CONSTANTS } from '@core/constants';
import { SessionService } from '@core/services/session/session.service';

@Component({
  selector: 'app-timer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './timer.component.html',
  styleUrls: ['./timer.component.scss']
})
export class TimerComponent implements OnInit, OnDestroy, AfterViewInit {
  // Display a fixed 45-minute window when <= 45min remain
  totalTimeInSeconds = 2700; // 45 min window
  remainingTime = this.totalTimeInSeconds; // remaining within window
  showTimer = false;
  private intervalId: any;

  @ViewChild('progressPath') progressPathRef?: ElementRef<SVGPathElement>;
  circumference = 2 * Math.PI * 16; // fallback
  strokeDashoffset = 0;

  // Turn red/animate only in last 15 min for now
  private readonly warningThresholdSeconds = 900; // 15 min
  isWarning = false;

  constructor(
    private readonly session: SessionService,
    private readonly cdr: ChangeDetectorRef // ADDED
  ) {}

  ngOnInit(): void {
    this.loadTimesFromStorage();
    this.startTimer();
  }

  ngAfterViewInit(): void {
    // Defer to next tick to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      const el = this.progressPathRef?.nativeElement;
      if (el && typeof el.getTotalLength === 'function') {
        const length = el.getTotalLength();
        if (isFinite(length) && length > 0) {
          this.circumference = length;
          this.updateProgress();
          this.cdr.detectChanges(); // ensure view sync after async update
        }
      }
    });
  }

  ngOnDestroy(): void { if (this.intervalId) clearInterval(this.intervalId); }

  private loadTimesFromStorage(): void {
    const issuedAtRaw = localStorage.getItem(SESSION_CONSTANTS.TOKEN_ISSUED_AT);
    const expiryRaw = localStorage.getItem(SESSION_CONSTANTS.TOKEN_EXPIRY);
    const issuedAt = issuedAtRaw ? Number(issuedAtRaw) : NaN;
    const expiryAt = expiryRaw ? Number(expiryRaw) : NaN;
    const now = Date.now();

    if (Number.isFinite(issuedAt) && Number.isFinite(expiryAt) && expiryAt > now) {
      const remainingSec = Math.floor((expiryAt - now) / 1000);
      this.showTimer = remainingSec <= 2700; // only show in last 45 min
      this.totalTimeInSeconds = 2700;
      this.remainingTime = Math.min(2700, Math.max(0, remainingSec));
      this.isWarning = this.remainingTime <= this.warningThresholdSeconds;
    } else {
      this.showTimer = false;
    }
  }

  private startTimer(): void {
    this.updateProgress();
    this.intervalId = setInterval(() => {
      const issuedAtRaw = localStorage.getItem(SESSION_CONSTANTS.TOKEN_ISSUED_AT);
      const expiryRaw = localStorage.getItem(SESSION_CONSTANTS.TOKEN_EXPIRY);
      const issuedAt = issuedAtRaw ? Number(issuedAtRaw) : NaN;
      const expiryAt = expiryRaw ? Number(expiryRaw) : NaN;
      const now = Date.now();

      if (Number.isFinite(issuedAt) && Number.isFinite(expiryAt) && expiryAt > now) {
        const remainingSec = Math.floor((expiryAt - now) / 1000);
        this.showTimer = remainingSec <= 2700;
        this.totalTimeInSeconds = 2700;
        this.remainingTime = Math.min(2700, Math.max(0, remainingSec));
        this.isWarning = this.remainingTime <= this.warningThresholdSeconds;
      } else {
        this.showTimer = false;
        this.remainingTime = 0;
      }

      this.updateProgress();

      if (this.remainingTime <= 0) {
        clearInterval(this.intervalId);
        this.session.logout(true);
      }
    }, 1000);
  }

  onTimerClick(): void {
    if (this.remainingTime <= 900) {
      this.session.showWarningDialog$.next(true);
    }
  }

  private updateProgress(): void {
    const progress = this.totalTimeInSeconds > 0 ? (this.remainingTime / this.totalTimeInSeconds) : 0;
    this.strokeDashoffset = this.circumference * (1 - progress);
  }

  get formattedTime(): string {
    const minutes = Math.floor(this.remainingTime / 60);
    const seconds = this.remainingTime % 60;
    return `${this.pad(minutes)}:${this.pad(seconds)}`;
  }

  private pad(num: number): string { return num < 10 ? `0${num}` : num.toString(); }
}